import { AlertCircleIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';

import { CheckboxCard } from './CheckboxCard';
import { RouteHelperModal } from './RouteHelperModal';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import {
  MOD_ROUTES,
  MOD_ROUTE_HELPER,
  MOD_ROUTE_SECTION_HELPER_LEGACY,
  type ModRoute,
} from '@/constants/medicalOnDemand';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { MODFormData } from '@/hooks/useMODInquiryForm';
import { trackMODRouteToggled } from '@/util/analyticsEvents';

type RouteFieldProps = {
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
};

/**
 * Route Field (Direct-to-MSL)
 *
 * Multi-select group of MOD routes ("Where should we route your question?")
 * for the MOD inquiry form. Mirrors the Controller + toggle pattern from
 * ProductSelectDropdown and reuses CheckboxCard so selected/disabled states
 * stay visually identical to Product(s).
 *
 * Stacked once the MIL flag is on: side by side holds two options and not
 * three, and it weakens which description belongs to which checkbox.
 *
 * The helper link sits beside the heading rather than below the options.
 * Underneath, it reads as a footnote a reader has already passed by the time
 * they need it.
 */
export function RouteField({ control, errors, isDisabled = false }: RouteFieldProps) {
  const [isHelperOpen, setIsHelperOpen] = useState(false);

  /** Off restores what is live today, field for field. */
  const isMilEnabled = useFeatureFlag(FEATURE_FLAGS.MOD_MIL_ROUTE);

  const routes = isMilEnabled ? MOD_ROUTES : MOD_ROUTES.filter((route) => route.id !== 'mil');

  return (
    <Controller
      name="routes"
      control={control}
      render={({ field: { onChange, value }, fieldState, formState }) => {
        const selected = (value ?? []) as ModRoute[];

        const showError =
          !!errors.routes?.message && (fieldState.isTouched || formState.isSubmitted);

        const toggleRoute = (routeId: ModRoute) => {
          if (isDisabled) {
            return;
          }

          const isSelected = selected.includes(routeId);

          const next = isSelected
            ? selected.filter((id) => id !== routeId)
            : [...selected, routeId];

          onChange(next);

          Promise.resolve()
            .then(() =>
              trackMODRouteToggled({
                route: routeId,
                action: isSelected ? 'deselected' : 'selected',
              })
            )
            .catch(() => undefined);
        };

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <VStack className="gap-1">
              {/*
                Column below md, row at md and up. A row with no wrap and an
                unflexed heading next to a shrink-0 link clips off the right
                edge on native once the heading needs a second line — RN-Web
                reflows it and looks fine, real native does not. Stacking
                deliberately below md avoids that rather than bounding the
                heading's width with flex-1, which squeezed every wrapped
                line to the same narrow column even the ones with nothing
                beside them.
              */}
              <VStack className="gap-1 md:flex-row md:items-baseline md:justify-between md:gap-3">
                <Text className="font-semibold text-sm text-typography-950">
                  Where should we route your question?
                </Text>
                {isMilEnabled ? (
                  /*
                    A step darker than the link color used elsewhere here,
                    which fails AA in dark mode. The rest of the app's links
                    share that gap and want their own sweep.
                  */
                  <LinkText
                    size="sm"
                    testID="mod-route-helper-link"
                    className="text-primary-600 md:shrink-0"
                    onPress={() => setIsHelperOpen(true)}>
                    {MOD_ROUTE_HELPER.linkLabel}
                  </LinkText>
                ) : null}
              </VStack>

              {isMilEnabled ? null : (
                <Text className="text-sm text-typography-600">
                  {MOD_ROUTE_SECTION_HELPER_LEGACY}
                </Text>
              )}
            </VStack>

            {/*
              No flex-1 on a stacked card. It makes every card a flex item with
              a zero basis, so spare height in the parent lands as uneven space
              between cards rather than inside them.
            */}
            <HStack
              className={
                isMilEnabled
                  ? 'mt-3 w-full flex-col items-stretch gap-3'
                  : 'mt-3 w-full flex-col items-stretch gap-3 md:flex-row md:items-start'
              }>
              {routes.map((route) => (
                <CheckboxCard
                  key={route.id}
                  testID={`mod-route-${route.id}`}
                  label={route.label}
                  description={route.description}
                  descriptionPlacement={isMilEnabled ? 'inside' : 'below'}
                  isSelected={selected.includes(route.id)}
                  isDisabled={isDisabled}
                  onSelect={() => toggleRoute(route.id)}
                  className={isMilEnabled ? undefined : 'flex-1'}
                />
              ))}
            </HStack>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.routes?.message}</FormControlErrorText>
            </FormControlError>

            {isMilEnabled ? (
              <RouteHelperModal isOpen={isHelperOpen} onClose={() => setIsHelperOpen(false)} />
            ) : null}
          </FormControl>
        );
      }}
    />
  );
}
